import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Alert,
  IconButton
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { getVoicePackageCountries } from '../../../../services/voicePackageApi';

const VoiceTrafficSettingsDialog = () => {
  const { t } = useTranslation();
  const {
    voiceTrafficSettingsOpen,
    setVoiceTrafficSettingsOpen,
    voiceTrafficCountriesMinutes = { 'UA': 1000 },
    voiceTrafficUseCustomAmount = false,
    voiceTrafficCustomAmount = 0,
    updateVoiceTrafficCountriesMinutes,
    updateVoiceTrafficUseCustomAmount,
    updateVoiceTrafficCustomAmount,
    addVoiceTrafficCountry,
    removeVoiceTrafficCountry,
    getVoiceTrafficCountries,
    hasVoiceTrafficCustomSettings,
    setHasVoiceTrafficCustomSettings,
    region
  } = useCalculator();

  // Local state for dialog
  const [localCountriesMinutes, setLocalCountriesMinutes] = useState(voiceTrafficCountriesMinutes);
  const [localUseCustomAmount, setLocalUseCustomAmount] = useState(voiceTrafficUseCustomAmount);
  const [availableCountryCodes, setAvailableCountryCodes] = useState(null); // null = loading

  // Available countries
  const availableCountries = [
    { code: 'UA', name: t('countries.ukraine', 'Ukraine'), currency: 'UAH', flag: '🇺🇦' },
    { code: 'KZ', name: t('countries.kazakhstan', 'Kazakhstan'), currency: 'KZT', flag: '🇰🇿' },
    { code: 'PL', name: t('countries.poland', 'Poland'), currency: 'EUR', flag: '🇵🇱' }
  ];

  // Sync with global state when dialog opens (no masking to avoid flicker)
  useEffect(() => {
    if (voiceTrafficSettingsOpen) {
      setLocalCountriesMinutes(voiceTrafficCountriesMinutes);
      setLocalUseCustomAmount(voiceTrafficUseCustomAmount);
    }
  }, [voiceTrafficSettingsOpen, voiceTrafficCountriesMinutes, voiceTrafficUseCustomAmount]);

  // Fetch available countries (those with active packages) when dialog opens
  useEffect(() => {
    if (voiceTrafficSettingsOpen) {
      getVoicePackageCountries()
        .then(res => {
          setAvailableCountryCodes(res.data?.data || []);
        })
        .catch(err => {
          console.error('Failed to fetch voice package countries:', err);
          setAvailableCountryCodes(['UA', 'KZ']); // fallback to known countries
        });
    }
  }, [voiceTrafficSettingsOpen]);

  const handleClose = () => {
    setVoiceTrafficSettingsOpen(false);
  };

  const handleSave = () => {
    // Consider packages enabled only when user explicitly selects any country
    const countries = Object.keys(localCountriesMinutes);
    const hasCustomSettings = countries.length > 0;

    // Update hasCustomSettings BEFORE updating data to prevent race condition
    setHasVoiceTrafficCustomSettings(hasCustomSettings);

    // Then update global state
    updateVoiceTrafficCountriesMinutes(localCountriesMinutes);
    updateVoiceTrafficUseCustomAmount(localUseCustomAmount);
    
    handleClose();
  };

  const handleReset = () => {
    setLocalCountriesMinutes({ 'UA': 500 });
    setLocalUseCustomAmount(false);
  };

  const handleCountryToggle = (countryCode) => {
    setLocalCountriesMinutes(prev => {
      const newCountriesMinutes = { ...prev };
      
      if (countryCode in newCountriesMinutes) {
        // Allow removing the last country (zero countries is valid)
        delete newCountriesMinutes[countryCode];
      } else {
        // Add country with default 500 minutes
        newCountriesMinutes[countryCode] = 500;
      }
      
      return newCountriesMinutes;
    });
  };

  const getCountryInfo = (code) => {
    return availableCountries.find(country => country.code === code);
  };

  return (
    <Dialog
      open={voiceTrafficSettingsOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('calculator.voiceTrafficSettings', 'Voice Traffic Settings')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Countries Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('calculator.voiceTrafficCountries', 'Countries')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('calculator.voiceTrafficCountriesDescription', 'Select countries for voice traffic packages. You can select multiple countries.')}
          </Typography>
          
          <Grid container spacing={1}>
            {availableCountries.map((country) => {
              const isSelected = country.code in localCountriesMinutes;
              const isDisabled = availableCountryCodes !== null && !availableCountryCodes.includes(country.code);
              return (
                <Grid item key={country.code}>
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                      </Box>
                    }
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    onClick={isDisabled ? undefined : () => handleCountryToggle(country.code)}
                    disabled={isDisabled}
                    sx={{
                      borderColor: isDisabled ? '#e0e0e0' : (isSelected ? '#00C853' : '#e5e7eb'),
                      backgroundColor: isDisabled ? '#f5f5f5' : (isSelected ? '#00C853' : 'white'),
                      color: isDisabled ? '#bdbdbd' : (isSelected ? 'white' : '#6b7280'),
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        backgroundColor: isDisabled ? '#f5f5f5' : (isSelected ? '#00A843' : '#f0fdf4'),
                        borderColor: isDisabled ? '#e0e0e0' : '#00C853',
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          {availableCountryCodes !== null && availableCountries.some(c => !availableCountryCodes.includes(c.code)) && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('voicePackages.noPackagesHint', 'Some countries are unavailable — no active minute packages configured')}
            </Typography>
          )}

          {Object.keys(localCountriesMinutes).length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('calculator.voiceTrafficSelectedCountries', 'Selected countries: {{countries}}', {
                countries: Object.keys(localCountriesMinutes).map(code => getCountryInfo(code)?.name).join(', ')
              })}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Custom Amount Option */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localUseCustomAmount}
                onChange={(e) => setLocalUseCustomAmount(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#00C853',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#00C853',
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('calculator.voiceTrafficCustomAmount', 'Deposit for outbound calls')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('calculator.voiceTrafficCustomAmountDescription', 'Use a custom amount instead of automatic package selection')}
                </Typography>
              </Box>
            }
          />

        </Box>

        {/* Summary */}
        {!localUseCustomAmount && Object.keys(localCountriesMinutes).length > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('calculator.voiceTrafficSummary', 'The system will automatically select the best packages for {{count}} countries based on your minute requirements.', {
              count: Object.keys(localCountriesMinutes).length
            })}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleReset}
          color="inherit"
          sx={{
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': {
              backgroundColor: '#f3f4f6',
            }
          }}
        >
          {t('common.reset', 'Reset')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{
            textTransform: 'none',
            color: '#374151',
            '&:hover': {
              backgroundColor: '#f9fafb',
            }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#00C853',
            color: 'white',
            '&:hover': {
              backgroundColor: '#00A843',
            },
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            }
          }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoiceTrafficSettingsDialog;